import { useCallback, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { CheckCircle2, FileVideo, Search, Upload, XCircle } from 'lucide-react';
import { db } from '@/db/schema';
import { normalizeText } from '@/lib/text';
import { formatBytes } from '@/lib/format';
import { importManualMedia, removeLocalMedia } from '@/services/media/manualImport';
import { mediaWriter } from '@/storage/index';
import type { Sign } from '@/types/catalog';

interface ImportOutcome {
  fileName: string;
  signName?: string;
  ok: boolean;
  message: string;
}

/**
 * Importação manual de mídia (PLAN §23).
 *
 * Existe porque a fonte CCB só publica vídeos no YouTube: sem uma origem autorizada
 * de MP4, este é o único caminho para ter o glossário completo offline.
 *
 * Dois modos:
 *  - **um sinal por vez**, escolhendo o sinal na busca;
 *  - **em lote**, casando pelo nome do arquivo (`<id-do-sinal>.mp4`).
 *
 * O pacote ZIP previsto no plano é opcional e não foi implementado: exigiria uma
 * dependência de descompactação para resolver um problema que a seleção múltipla de
 * arquivos já resolve, sem biblioteca nenhuma.
 */
export function ManualImportPanel(): React.ReactElement {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Sign | null>(null);
  const [outcomes, setOutcomes] = useState<ImportOutcome[]>([]);
  const [busy, setBusy] = useState(false);

  const singleInputRef = useRef<HTMLInputElement>(null);
  const batchInputRef = useRef<HTMLInputElement>(null);

  const matches = useLiveQuery(async () => {
    const term = normalizeText(query);
    if (term.length < 2) return [];

    const signs = await db.signs.toArray();
    return signs.filter((sign) => sign.normalizedName.includes(term)).slice(0, 8);
  }, [query]);

  const selectedState = useLiveQuery(
    async () => (selected ? db.localSignState.get(selected.id) : undefined),
    [selected?.id],
  );

  const importSingle = useCallback(
    async (file: File) => {
      if (!selected) return;
      setBusy(true);

      try {
        const alreadyHasFile = selectedState?.offline.status === 'available';
        const result = await importManualMedia(selected.id, file, mediaWriter(), {
          replace: alreadyHasFile,
        });

        setOutcomes([
          {
            fileName: file.name,
            signName: selected.name,
            ok: result.ok,
            message: result.ok
              ? `${formatBytes(result.bytes)} importados.`
              : result.issues.map((issue) => issue.message).join(' '),
          },
        ]);
      } finally {
        setBusy(false);
        if (singleInputRef.current) singleInputRef.current.value = '';
      }
    },
    [selected, selectedState],
  );

  /**
   * Importa vários arquivos casando pelo nome (F17-T09 em versão sem ZIP).
   *
   * Todos os arquivos são casados com sinais **antes** de qualquer gravação: um lote
   * com nome errado não deve deixar metade importada (F17-T10).
   */
  const importBatch = useCallback(async (files: FileList) => {
    setBusy(true);

    try {
      const entries = [...files];
      const ids = entries.map((file) => file.name.replace(/\.[^.]+$/, ''));
      const signs = await db.signs.bulkGet(ids);

      const unmatched = entries.filter((_file, index) => signs[index] === undefined);
      if (unmatched.length > 0) {
        setOutcomes(
          unmatched.map((file) => ({
            fileName: file.name,
            ok: false,
            message:
              'Nenhum sinal com este identificador. O arquivo precisa se chamar ' +
              '<id-do-sinal>.mp4. Nada foi importado.',
          })),
        );
        return;
      }

      const results: ImportOutcome[] = [];
      for (const [index, file] of entries.entries()) {
        const sign = signs[index]!;
        const result = await importManualMedia(sign.id, file, mediaWriter(), { replace: true });

        results.push({
          fileName: file.name,
          signName: sign.name,
          ok: result.ok,
          message: result.ok
            ? `${formatBytes(result.bytes)} importados.`
            : result.issues.map((issue) => issue.message).join(' '),
        });
      }

      setOutcomes(results);
    } finally {
      setBusy(false);
      if (batchInputRef.current) batchInputRef.current.value = '';
    }
  }, []);

  const hasFile = selectedState?.offline.status === 'available';

  return (
    <section className="rounded-xl bg-slate-800 p-4">
      <h2 className="flex items-center gap-2 font-medium">
        <FileVideo size={18} aria-hidden />
        Importar vídeos do dispositivo
      </h2>

      <p className="mt-1 text-sm text-slate-400">
        Associe arquivos de vídeo que você já tem aos sinais do glossário, para
        assisti-los sem internet.
      </p>

      {/* --- um sinal por vez --- */}
      <div className="mt-4">
        <label htmlFor="import-search" className="text-sm font-medium">
          1. Escolha o sinal
        </label>

        <div className="mt-1 flex min-h-11 items-center gap-2 rounded-lg bg-slate-900 px-3">
          <Search size={16} className="shrink-0 text-slate-500" aria-hidden />
          <input
            id="import-search"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelected(null);
            }}
            placeholder="Nome do sinal"
            autoComplete="off"
            className="min-w-0 flex-1 bg-transparent py-2 text-base outline-none placeholder:text-slate-600"
          />
        </div>

        {!selected && matches && matches.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1">
            {matches.map((sign) => (
              <li key={sign.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(sign);
                    setQuery(sign.name);
                  }}
                  className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm active:bg-slate-700"
                >
                  {sign.name}
                </button>
              </li>
            ))}
          </ul>
        )}

        {selected && (
          <div className="mt-3">
            <p className="text-sm font-medium">2. Escolha o arquivo</p>
            <p className="mt-0.5 text-xs text-slate-400">
              Sinal selecionado: {selected.name}
              {hasFile && ' — já existe um arquivo; ele será substituído.'}
            </p>

            <input
              ref={singleInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importSingle(file);
              }}
              className="mt-2 block w-full text-sm text-slate-400 file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-sky-500 file:px-4 file:text-sm file:font-medium file:text-slate-950"
            />

            {hasFile && (
              <button
                type="button"
                onClick={() => void removeLocalMedia(selected.id, mediaWriter())}
                className="mt-2 min-h-11 rounded-lg px-3 text-sm text-slate-400 active:bg-slate-700"
              >
                Remover arquivo deste sinal
              </button>
            )}
          </div>
        )}
      </div>

      {/* --- em lote --- */}
      <div className="mt-5 border-t border-slate-700 pt-4">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Upload size={16} aria-hidden />
          Importar vários de uma vez
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          Cada arquivo precisa se chamar <code className="text-slate-300">&lt;id-do-sinal&gt;.mp4</code>.
          Se algum nome não casar, nada é importado.
        </p>

        <input
          ref={batchInputRef}
          type="file"
          multiple
          accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
          disabled={busy}
          onChange={(event) => {
            const files = event.target.files;
            if (files && files.length > 0) void importBatch(files);
          }}
          className="mt-2 block w-full text-sm text-slate-400 file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-slate-700 file:px-4 file:text-sm file:font-medium file:text-slate-200"
        />
      </div>

      {busy && <p className="mt-3 text-sm text-slate-400">Validando e importando…</p>}

      {outcomes.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2" role="status" aria-label="Resultado da importação">
          {outcomes.map((outcome) => (
            <li key={outcome.fileName} className="flex items-start gap-2 text-sm">
              {outcome.ok ? (
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-400" aria-hidden />
              ) : (
                <XCircle size={16} className="mt-0.5 shrink-0 text-red-400" aria-hidden />
              )}
              <span className="min-w-0">
                <span className="font-medium">{outcome.signName ?? outcome.fileName}</span>
                <span className="block text-slate-400">{outcome.message}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
